import React, { useMemo, useRef, useState } from 'react';
import { FiExternalLink } from 'react-icons/fi';
import api, { ExpensesAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { LoaderInline, Spinner } from './Loader';

const colors = {
  stone100: '#f5f5f4',
  stone200: '#e7e5e4',
  stone500: '#78716c',
  stone700: '#44403c',
  stone800: '#292524',
  emerald600: '#059669',
  rose600: '#e11d48',
  amber600: '#d97706',
  blue600: '#2563eb'
};

function emptySummary() {
  return { uploaded: 0, extracted: 0, created: 0, duplicates: 0, failed: 0 };
}

function fileKey(file) {
  return `${file?.name || ''}__${file?.size || 0}__${file?.lastModified || 0}`;
}

function extractS3KeyFromUrl(url) {
  const raw = String(url || '').trim();
  if (!raw) return '';
  try {
    const parsed = new URL(raw);
    return decodeURIComponent(String(parsed.pathname || '').replace(/^\/+/, '').trim());
  } catch (_) {
    return '';
  }
}

async function uploadViaPresigned(file, dir) {
  const { data: presigned } = await api.get('/api/upload/presigned-url', { params: { fileType: file.type, dir } });
  await fetch(presigned.uploadUrl, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file });
  return {
    fileUrl: presigned.fileUrl,
    fileType: file.type,
    receiptS3Key: extractS3KeyFromUrl(presigned.fileUrl)
  };
}

async function sha256File(file) {
  const arr = await file.arrayBuffer();
  const digest = await window.crypto.subtle.digest('SHA-256', arr);
  const bytes = new Uint8Array(digest);
  return Array.from(bytes).map((b) => b.toString(16).padStart(2, '0')).join('');
}

async function countPdfPages(file) {
  if (!file?.name?.toLowerCase().endsWith('.pdf')) return 1;
  try {
    const text = await file.text();
    const matches = text.match(/\/Type\s*\/Page[^s]/g);
    return matches ? matches.length : 1;
  } catch (_) {
    return 1;
  }
}

export default function BulkUploadModal({ isOpen, onClose, onCreated, onViewExpense }) {
  const inputRef = useRef(null);
  const { company } = useAuth();
  const companyId = company?.id || null;

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [running, setRunning] = useState(false);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(emptySummary());

  const canRun = useMemo(() => selectedFiles.length > 0 && !running, [selectedFiles.length, running]);

  const handlePick = (files) => {
    const incoming = Array.from(files || []).filter((f) => {
      const t = String(f.type || '').toLowerCase();
      const n = String(f.name || '').toLowerCase();
      return t.startsWith('image/') || t.includes('pdf') || n.endsWith('.pdf');
    });

    if (!incoming.length) return;

    setSelectedFiles((prev) => {
      const seen = new Set((prev || []).map((f) => fileKey(f)));
      const uniqueIncoming = incoming.filter((f) => !seen.has(fileKey(f)));
      return [...(prev || []), ...uniqueIncoming];
    });

    setRows((prev) => {
      const existingIds = new Set((prev || []).map((r) => r.id));
      const nextRows = [...(prev || [])];
      for (const f of incoming) {
        const id = fileKey(f);
        if (existingIds.has(id)) continue;
        nextRows.push({
          id,
          name: f.name,
          status: 'queued',
          message: '',
          duplicateExpenseId: null
        });
      }
      return nextRows;
    });

    setSummary(emptySummary());
  };

  const runBulkUpload = async () => {
    if (!companyId || !canRun) return;
    setRunning(true);
    const dir = `expenses/${companyId}`;
    const statuses = rows.slice();
    const setRow = (id, patch) => {
      const idx = statuses.findIndex((r) => r.id === id);
      if (idx < 0) return;
      statuses[idx] = { ...statuses[idx], ...patch };
      setRows(statuses.slice());
    };

    try {
      const fileMeta = await Promise.all(selectedFiles.map(async (file) => ({
        id: fileKey(file),
        file,
        receiptSha256: await sha256File(file),
        pageCount: await countPdfPages(file)
      })));
      const duplicateResp = await ExpensesAPI.checkDuplicates(fileMeta.map((m) => m.receiptSha256));
      const duplicateMap = duplicateResp?.duplicates || {};
      const uploadableMeta = [];
      let duplicates = 0;
      for (const meta of fileMeta) {
        const duplicate = duplicateMap[String(meta.receiptSha256 || '').toLowerCase()];
        if (duplicate) {
          setRow(meta.id, { status: 'duplicate', message: `Duplicate • Inv #${duplicate?.id || '-'}`, duplicateExpenseId: duplicate?.id || null });
          duplicates += 1;
          continue;
        }
        uploadableMeta.push(meta);
      }
      const uploadedPayloads = [];
      for (let i = 0; i < uploadableMeta.length; i += 1) {
        const meta = uploadableMeta[i];
        setRow(meta.id, { status: 'uploading', message: 'Uploading...' });
        // eslint-disable-next-line no-await-in-loop
        const up = await uploadViaPresigned(meta.file, dir);
        uploadedPayloads.push({ ...up, id: meta.id, receiptSha256: meta.receiptSha256, pageCount: meta.pageCount });
        setRow(meta.id, { status: 'uploaded', message: 'Uploaded' });
      }
      setSummary((s) => ({ ...s, uploaded: uploadedPayloads.length, duplicates }));

      const queued = await ExpensesAPI.bulkUpload(uploadedPayloads.map((p) => ({
        fileUrl: p.fileUrl,
        fileType: p.fileType,
        receiptS3Key: p.receiptS3Key || null,
        receiptSha256: p.receiptSha256,
        pageCount: p.pageCount
      })), {
        usagePaymentId: undefined
      });
      const batchId = Number(queued?.batchId || 0);
      if (!batchId) throw new Error('Unable to start bulk upload batch');

      const byIndex = new Map(uploadedPayloads.map((payload, idx) => [idx, payload]));
      const byHash = new Map(uploadedPayloads.map((payload) => [String(payload.receiptSha256 || '').toLowerCase(), payload]));
      for (const payload of uploadedPayloads) {
        setRow(payload.id, { status: 'queued', message: 'Queued for OCR...' });
      }

      let done = false;
      let finalSummary = { created: 0, duplicates, failed: 0 };
      while (!done) {
        // eslint-disable-next-line no-await-in-loop
        const statusResp = await ExpensesAPI.getBulkUploadStatus(batchId);
        const serverFiles = Array.isArray(statusResp?.files) ? statusResp.files : [];
        const mapStatus = (serverStatus) => {
          switch (String(serverStatus || '').toUpperCase()) {
            case 'QUEUED':
              return { status: 'queued', message: 'Queued for OCR...' };
            case 'EXTRACTING':
              return { status: 'uploading', message: 'Extracting...' };
            case 'EXTRACTED':
              return { status: 'uploading', message: 'Extracted' };
            case 'CREATING':
              return { status: 'creating', message: 'Creating draft expense...' };
            case 'CREATED':
              return { status: 'done', message: 'Draft created' };
            case 'DUPLICATE':
              return { status: 'duplicate', message: 'Duplicate receipt' };
            case 'FAILED':
              return { status: 'failed', message: 'Processing failed' };
            default:
              return { status: 'queued', message: 'Queued...' };
          }
        };

        for (const file of serverFiles) {
          const statusPatch = mapStatus(file.status);
          const hashKey = String(file.receiptSha256 || '').toLowerCase();
          const uploadedMeta = byIndex.get(Number(file.index)) || (hashKey ? byHash.get(hashKey) : null);
          if (!uploadedMeta) continue;
          const rowPatch = {
            ...statusPatch,
            message: file.error || statusPatch.message
          };
          if (String(file.status || '').toUpperCase() === 'DUPLICATE') {
            rowPatch.duplicateExpenseId = Number(file.expenseId || 0) || null;
            rowPatch.message = file.expenseId ? `Duplicate • Inv #${file.expenseId}` : (file.error || 'Duplicate receipt');
          }
          if (String(file.status || '').toUpperCase() === 'CREATED') {
            rowPatch.duplicateExpenseId = Number(file.expenseId || 0) || null;
          }
          setRow(uploadedMeta.id, rowPatch);
        }

        const serverDuplicates = Number(statusResp?.duplicateCount || 0);
        finalSummary = {
          created: Number(statusResp?.createdCount || 0),
          duplicates: duplicates + serverDuplicates,
          failed: Number(statusResp?.failedCount || 0)
        };
        setSummary({
          uploaded: uploadedPayloads.length,
          extracted: Number(statusResp?.extractedCount || 0),
          created: finalSummary.created,
          duplicates: finalSummary.duplicates,
          failed: finalSummary.failed
        });

        const batchStatus = String(statusResp?.status || '').toUpperCase();
        done = batchStatus === 'COMPLETED' || batchStatus === 'FAILED';
        if (!done) {
          // eslint-disable-next-line no-await-in-loop
          await new Promise((resolve) => setTimeout(resolve, 3000));
        }
      }

      if (onCreated) onCreated(finalSummary);
    } catch (err) {
      window.alert(err?.response?.data?.error || err.message || 'Bulk upload failed');
    }

    setRunning(false);
  };

  if (!isOpen) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={() => !running && onClose?.()}>
      <div style={{ width: 860, maxWidth: '96vw', maxHeight: '90vh', overflow: 'auto', background: '#fff', borderRadius: 12, border: `1px solid ${colors.stone200}` }} onClick={(e) => e.stopPropagation()}>
        <div style={{ padding: 14, borderBottom: `1px solid ${colors.stone200}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 style={{ margin: 0, color: colors.stone800 }}>Bulk Upload Expenses</h3>
          <button className="btn btn-secondary" onClick={() => !running && onClose?.()}>Close</button>
        </div>

        <div style={{ padding: 14 }}>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept="image/jpeg,image/png,image/jpg,image/webp,application/pdf"
            style={{ display: 'none' }}
            onChange={(e) => {
              handlePick(e.target.files);
              e.target.value = '';
            }}
          />
          <div style={{ border: `2px dashed ${colors.stone200}`, borderRadius: 12, padding: 24, textAlign: 'center', background: colors.stone100 }}>
            <div style={{ fontSize: 14, color: colors.stone700 }}>Select multiple receipts/invoices (images or PDFs)</div>
            <button className="btn btn-secondary" style={{ marginTop: 10 }} onClick={() => inputRef.current?.click()} disabled={running}>Choose Files</button>
          </div>

          {rows.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 12, color: colors.stone500, marginBottom: 8 }}>
                Uploaded: {summary.uploaded} | Extracted: {summary.extracted} | Drafts created: {summary.created} | Duplicates: {summary.duplicates} | Failed: {summary.failed}
              </div>
              <div style={{ border: `1px solid ${colors.stone200}`, borderRadius: 10, overflow: 'hidden' }}>
                {rows.map((r) => (
                  <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, borderTop: `1px solid ${colors.stone200}`, padding: '8px 10px', fontSize: 13 }}>
                    <div style={{ color: colors.stone800, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {(r.status === 'uploading' || r.status === 'creating') && <Spinner size={13} color={colors.amber600} />}
                      <div style={{ color: r.status === 'done' ? colors.emerald600 : (r.status === 'duplicate' ? colors.stone700 : (r.status === 'failed' ? colors.rose600 : (r.status === 'uploading' || r.status === 'creating' ? colors.amber600 : colors.blue600))) }}>
                        {r.message || r.status}
                      </div>
                      {r.status === 'duplicate' && r.duplicateExpenseId && (
                        <button
                          type="button"
                          className="btn btn-secondary"
                          style={{ fontSize: 11, padding: '3px 7px', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                          onClick={() => onViewExpense?.(r.duplicateExpenseId)}
                        >
                          <FiExternalLink size={12} /> View
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div style={{ padding: 14, borderTop: `1px solid ${colors.stone200}`, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn btn-secondary" onClick={() => !running && onClose?.()} disabled={running}>Cancel</button>
          <button className="submit-button" onClick={runBulkUpload} disabled={!canRun}>
            {running ? <LoaderInline label="Processing" color="#fff" /> : 'Start Bulk Upload'}
          </button>
        </div>
      </div>
    </div>
  );
}
